import { computed, reactive, watch, onScopeDispose } from 'vue';
import { useStore } from 'vuex';
import { requestPeopleTags } from '@/api/peopleTagsApi';
import { tagPersonKey } from '../utils/peopleTagUtils';

export const peopleTagsState = reactive({ available: false, loaded: false, tags: [], people: {}, roles: [], capabilities: {}, error: '', revision: 0 });
export const peopleTagsModal = reactive({ visible: false, targets: [], name: '', focusTagId: null, catalog: false, draft: null });
const subscribers = new Map();
const personSequences = new Map();
let pending = new Map(), timer = null, generation = 0, ownerKey = '', requestSequence = 0, appliedCatalogSequence = 0;
function reset() {
  generation++; personSequences.clear(); pending.clear(); clearTimeout(timer); timer = null;
  Object.assign(peopleTagsState, { available: false, loaded: false, tags: [], people: {}, roles: [], capabilities: {}, error: '' });
  peopleTagsModal.visible = false;
  peopleTagsModal.draft = null;
}
function merge(data, epoch, sequence) {
  if (epoch !== generation) return;
  if (sequence >= appliedCatalogSequence) {
    appliedCatalogSequence = sequence;
    Object.assign(peopleTagsState, { available: data.available, loaded: true, tags: data.tags || [], roles: data.roles || [], capabilities: data.capabilities || {}, error: '' });
  }
  for (const [key, person] of Object.entries(data.people || {})) {
    if (sequence >= (personSequences.get(key) || 0)) { peopleTagsState.people[key] = person; personSequences.set(key, sequence); }
  }
}
export async function fetchPeopleTags(payload = {}) {
  const epoch = generation, sequence = ++requestSequence;
  try {
    const data = await requestPeopleTags(payload);
    merge(data, epoch, sequence);
    return epoch === generation ? data : null;
  } catch (error) {
    if (epoch === generation) peopleTagsState.error = error?.response?.data?.error?.message || error.message || 'Could not load tags.';
    throw error;
  }
}
async function flush() {
  timer = null;
  const targets = [...pending.values()]; pending.clear();
  for (let i = 0; i < targets.length; i += 100) {
    try { await fetchPeopleTags({ action: 'read', targets: targets.slice(i, i + 100) }); } catch { /* Retry on focus or in the modal. */ }
  }
}
export function queuePersonTags(target, force = false) {
  const key = tagPersonKey(target);
  if (!key || (!force && peopleTagsState.people[key])) return;
  pending.set(key, target);
  if (!timer) timer = setTimeout(flush, 25);
}
export function refreshPeopleTags() {
  generation++;
  personSequences.clear();
  peopleTagsState.revision++;
  for (const entry of subscribers.values()) queuePersonTags(entry, true);
}
export function openPeopleTags({ targets = [], name = '', focusTagId = null, catalog = false } = {}) {
  Object.assign(peopleTagsModal, { visible: true, targets, name, focusTagId, catalog, draft: null });
}
export function openDraftPeopleTags({ kind = 'employee', tagIds = [], name = '', focusTagId = null, onCommit } = {}) {
  Object.assign(peopleTagsModal, {
    visible: true, targets: [], name, focusTagId, catalog: false,
    draft: { kind, tagIds: tagIds.map(Number).filter(id => id > 0), onCommit }
  });
}
export function usePeopleTagsSession() {
  const store = useStore();
  watch(() => JSON.stringify([store.state.users?.user?.id, store.state.users?.user?.token,
    store.state.users?.user?.roles, store.state.users?.user?.permissions]), key => {
    if (ownerKey === key) return;
    ownerKey = key; reset();
    if (store.state.users?.user?.isLogged) {
      for (const target of subscribers.values()) queuePersonTags(target, true);
    }
  }, { immediate: true, flush: 'sync' });
  let lastRefresh = 0;
  const onReturn = () => {
    if (document.visibilityState === 'hidden' || !store.state.users?.user?.isLogged || Date.now() - lastRefresh < 15000) return;
    lastRefresh = Date.now(); refreshPeopleTags();
  };
  const onStorage = event => {
    if (event.key !== 'hr:data-changed:v1') return;
    try { if (JSON.parse(event.newValue || '{}').domains?.includes('people-tags')) { lastRefresh = 0; onReturn(); } } catch { /* Ignore unrelated storage. */ }
  };
  const refreshTimer = setInterval(onReturn, 60000);
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', onReturn);
  document.addEventListener('visibilitychange', onReturn);
  onScopeDispose(() => { clearInterval(refreshTimer); window.removeEventListener('storage', onStorage); window.removeEventListener('focus', onReturn); document.removeEventListener('visibilitychange', onReturn); });
}
export function usePersonTags(targetRef) {
  const subscription = Symbol('person-tags');
  watch(targetRef, target => {
    if (target) { subscribers.set(subscription, target); queuePersonTags(target); }
    else subscribers.delete(subscription);
  }, { immediate: true });
  onScopeDispose(() => subscribers.delete(subscription));
  const person = computed(() => peopleTagsState.people[tagPersonKey(targetRef.value)]);
  return { person, tags: computed(() => peopleTagsState.tags.filter(tag => tag.is_active && person.value?.tags?.includes(tag.id))) };
}
